import { isPlainObject } from 'lodash-es'
import type { FormFieldFilterType, FormFieldsType } from '@/services/forms/types'
import { TmFormError } from '@/core/error/tmFormError'
import { getLoggerService } from '@/core/container/ioc'
import type LoggerService from '@/services/loggerService'
import type { FilterFieldData } from '@/services/tables/filters/fields/filterField'

export default class BaseFilterForm<T extends FormFieldFilterType> {
  protected form: FormFieldsType<T>

  protected formId: string

  protected logger: LoggerService

  constructor(formId: string) {
    this.form = {}
    this.formId = formId

    this.logger = getLoggerService()
    this.log('Init BaseForm constructor')
  }

  public buildForm(fields: T[]) {
    for (const field of fields) {
      this.form[field.getName()] = field
    }

    this.log('build form')

    return this.form
  }

  public getRawForm(): FormFieldsType<T> {
    return this.form
  }

  public setFieldValue(name: string, value: unknown, setInitial = false) {
    const field = this.getFieldOrNull(name)
    if (!field) {
      const error = new TmFormError(`Field: ${name} is not added to the filter form ${this.formId}`)
      // eslint-disable-next-line no-console
      console.error(error, error.stack)
      return
    }

    const patch: Partial<FilterFieldData> = { value, isApplied: false }
    if (setInitial) {
      patch.initialValue = value
    }

    field.patch(patch)
  }

  public populate(data: { [key: string]: unknown }, setInitial = true) {
    for (const fieldName in data) {
      const field = this.getFieldOrNull(fieldName)
      const changes = data[fieldName]

      if (!field || !isPlainObject(changes)) {
        continue
      }

      const patchData = changes as Partial<FilterFieldData>
      if (setInitial && typeof patchData.value !== 'undefined' && typeof patchData.initialValue === 'undefined') {
        field.patch({ ...patchData, initialValue: patchData.value })
      } else {
        field.patch(patchData)
      }
    }
  }

  public getFields(): T[] {
    return Object.values(this.form)
  }

  public destroy() {
    this.log('destroy form')
    this.form = {}
  }

  // Set value with all hooks
  public onInput(name: string, value: any) {
    this.log(`onInput: ${name} ${value}`)
    this.setFieldValue(name, value)
  }

  public getField(name: string): T {
    const field = this.getFieldOrNull(name)
    if (!field) {
      throw new TmFormError(`Field: ${name} is not added to the form`)
    }

    return field
  }

  public getFieldOrNull(name: string): T | null {
    return this.form[name] ?? null
  }

  public getFieldById(id: string): T | null {
    return this.getFields().find((field) => field.id === id) ?? null
  }

  protected log(message: string) {
    this.logger.log('forms', message, this.formId)
  }
}
